import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse, HttpEvent } from '@angular/common/http';
import { CustomHttpUrlEncodingCodec } from '../encoder';
import { Observable } from 'rxjs';
import { CampaignGroupDetailDto } from '../model/campaignGroupDetailDto';
import { CampaignGroupListItemDto } from '../model/campaignGroupListItemDto';
import { CampaignGroupStatsDto } from '../model/campaignGroupStatsDto';
import { BASE_PATH } from '../variables';
import { Configuration } from '../configuration';

@Injectable()
export class EngineCampaignGroupService {

    protected basePath = 'https://dmp.airiane.com/services/engine';
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();

    constructor(
        protected httpClient: HttpClient,
        @Optional() @Inject(BASE_PATH) basePath: string,
        @Optional() configuration: Configuration
    ) {
        if (basePath) {
            this.basePath = basePath;
        }
        if (configuration) {
            this.configuration = configuration;
            this.basePath = basePath || configuration.basePath || this.basePath;
        }
    }

    public findAll(
        xDataStoreID: string,
        xPARTNERID?: string,
        search?: string,
        page?: number,
        size?: number,
        observe?: 'body',
        reportProgress?: boolean
    ): Observable<Array<CampaignGroupListItemDto>>;
    public findAll(
        xDataStoreID: string,
        xPARTNERID?: string,
        search?: string,
        page?: number,
        size?: number,
        observe?: 'response',
        reportProgress?: boolean
    ): Observable<HttpResponse<Array<CampaignGroupListItemDto>>>;
    public findAll(
        xDataStoreID: string,
        xPARTNERID?: string,
        search?: string,
        page?: number,
        size?: number,
        observe: any = 'body',
        reportProgress = false
    ): Observable<any> {
        if (xDataStoreID === null || xDataStoreID === undefined) {
            throw new Error('Required parameter xDataStoreID was null or undefined when calling findAll.');
        }

        let queryParameters = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
        if (search !== undefined && search !== null) {
            queryParameters = queryParameters.set('search', search as any);
        }
        if (page !== undefined && page !== null) {
            queryParameters = queryParameters.set('page', page as any);
        }
        if (size !== undefined && size !== null) {
            queryParameters = queryParameters.set('size', size as any);
        }

        let headers = this.defaultHeaders;
        if (xDataStoreID !== undefined && xDataStoreID !== null) {
            headers = headers.set('X-DataStoreID', String(xDataStoreID));
        }
        if (xPARTNERID !== undefined && xPARTNERID !== null) {
            headers = headers.set('X-PARTNER-ID', String(xPARTNERID));
        }

        const httpHeaderAccepts: string[] = ['application/json'];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        return this.httpClient.request<Array<CampaignGroupListItemDto>>(
            'get',
            `${this.basePath}/api/campaign-groups/`,
            {
                params: queryParameters,
                withCredentials: this.configuration.withCredentials,
                headers,
                observe,
                reportProgress
            }
        );
    }

    public get(xDataStoreID: string, id: number, observe?: 'body', reportProgress?: boolean): Observable<CampaignGroupDetailDto>;
    public get(xDataStoreID: string, id: number, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<CampaignGroupDetailDto>>;
    public get(xDataStoreID: string, id: number, observe: any = 'body', reportProgress = false): Observable<any> {
        if (xDataStoreID === null || xDataStoreID === undefined) {
            throw new Error('Required parameter xDataStoreID was null or undefined when calling get.');
        }
        if (id === null || id === undefined) {
            throw new Error('Required parameter id was null or undefined when calling get.');
        }

        let headers = this.defaultHeaders;
        headers = headers.set('X-DataStoreID', String(xDataStoreID));

        const httpHeaderAccepts: string[] = ['application/json'];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        return this.httpClient.request<CampaignGroupDetailDto>(
            'get',
            `${this.basePath}/api/campaign-groups/${encodeURIComponent(String(id))}`,
            {
                withCredentials: this.configuration.withCredentials,
                headers,
                observe,
                reportProgress
            }
        );
    }

    public getStats(xDataStoreID: string, id: number, observe?: 'body', reportProgress?: boolean): Observable<CampaignGroupStatsDto>;
    public getStats(xDataStoreID: string, id: number, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<CampaignGroupStatsDto>>;
    public getStats(xDataStoreID: string, id: number, observe: any = 'body', reportProgress = false): Observable<any> {
        if (xDataStoreID === null || xDataStoreID === undefined) {
            throw new Error('Required parameter xDataStoreID was null or undefined when calling getStats.');
        }
        if (id === null || id === undefined) {
            throw new Error('Required parameter id was null or undefined when calling getStats.');
        }

        let headers = this.defaultHeaders;
        headers = headers.set('X-DataStoreID', String(xDataStoreID));

        const httpHeaderAccepts: string[] = ['application/json'];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        return this.httpClient.request<CampaignGroupStatsDto>(
            'get',
            `${this.basePath}/api/campaign-groups/${encodeURIComponent(String(id))}/stats`,
            {
                withCredentials: this.configuration.withCredentials,
                headers,
                observe,
                reportProgress
            }
        );
    }

    public create(xDataStoreID: string, body: CampaignGroupDetailDto, observe?: 'body', reportProgress?: boolean): Observable<CampaignGroupDetailDto>;
    public create(xDataStoreID: string, body: CampaignGroupDetailDto, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<CampaignGroupDetailDto>>;
    public create(xDataStoreID: string, body: CampaignGroupDetailDto, observe: any = 'body', reportProgress = false): Observable<any> {
        if (xDataStoreID === null || xDataStoreID === undefined) {
            throw new Error('Required parameter xDataStoreID was null or undefined when calling create.');
        }
        if (body === null || body === undefined) {
            throw new Error('Required parameter body was null or undefined when calling create.');
        }

        let headers = this.defaultHeaders;
        headers = headers.set('X-DataStoreID', String(xDataStoreID));
        headers = headers.set('Content-Type', 'application/json');

        const httpHeaderAccepts: string[] = ['application/json'];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        return this.httpClient.request<CampaignGroupDetailDto>(
            'post',
            `${this.basePath}/api/campaign-groups/`,
            {
                body,
                withCredentials: this.configuration.withCredentials,
                headers,
                observe,
                reportProgress
            }
        );
    }

    public update(xDataStoreID: string, id: number, body: CampaignGroupDetailDto, observe?: 'body', reportProgress?: boolean): Observable<CampaignGroupDetailDto>;
    public update(xDataStoreID: string, id: number, body: CampaignGroupDetailDto, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<CampaignGroupDetailDto>>;
    public update(xDataStoreID: string, id: number, body: CampaignGroupDetailDto, observe: any = 'body', reportProgress = false): Observable<any> {
        if (xDataStoreID === null || xDataStoreID === undefined) {
            throw new Error('Required parameter xDataStoreID was null or undefined when calling update.');
        }
        if (id === null || id === undefined) {
            throw new Error('Required parameter id was null or undefined when calling update.');
        }
        if (body === null || body === undefined) {
            throw new Error('Required parameter body was null or undefined when calling update.');
        }

        let headers = this.defaultHeaders;
        headers = headers.set('X-DataStoreID', String(xDataStoreID));
        headers = headers.set('Content-Type', 'application/json');

        const httpHeaderAccepts: string[] = ['application/json'];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        return this.httpClient.request<CampaignGroupDetailDto>(
            'put',
            `${this.basePath}/api/campaign-groups/${encodeURIComponent(String(id))}`,
            {
                body,
                withCredentials: this.configuration.withCredentials,
                headers,
                observe,
                reportProgress
            }
        );
    }

    public delete(xDataStoreID: string, id: number, observe?: 'body', reportProgress?: boolean): Observable<any>;
    public delete(xDataStoreID: string, id: number, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<any>>;
    public delete(xDataStoreID: string, id: number, observe: any = 'body', reportProgress = false): Observable<any> {
        if (xDataStoreID === null || xDataStoreID === undefined) {
            throw new Error('Required parameter xDataStoreID was null or undefined when calling delete.');
        }
        if (id === null || id === undefined) {
            throw new Error('Required parameter id was null or undefined when calling delete.');
        }

        let headers = this.defaultHeaders;
        headers = headers.set('X-DataStoreID', String(xDataStoreID));

        return this.httpClient.request<any>(
            'delete',
            `${this.basePath}/api/campaign-groups/${encodeURIComponent(String(id))}`,
            {
                withCredentials: this.configuration.withCredentials,
                headers,
                observe,
                reportProgress
            }
        );
    }
}
